// Monthly streaming report for September 2026.
import { defineDocument, em, link, list, p, section, table, text, } from '../../schema';
export const report202609TopPartyGamesAndStreamersDocument = defineDocument({
    id: 'report:2026-09-top-party-games-and-streamers',
    kind: 'report',
    slug: '2026-09-top-party-games-and-streamers',
    route: '/party-central/reports/2026-09-top-party-games-and-streamers',
    seo: {
        title: 'September 2026: Top Party Games & Streamers on Twitch',
        description: 'Data-driven analysis of party game streaming on Twitch for September 2026. Party Score rankings, top games, streamers, language breakdowns, and new releases.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Party Central Report',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'September 2026',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Top Party Games & Streamers on Twitch',
            },
        ],
        background: 'gradient-card',
        backgroundAccentFamily: 'yellow',
        showDescription: true,
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-10-04T00:00:00.000Z'),
        updatedAt: new Date('2026-10-04T00:00:00.000Z'),
        reportMonth: '2026-09',
        dataRange: {
            start: '2026-09-01',
            end: '2026-09-30',
        },
    },
    aside: {
        showToc: true,
    },
    sections: [
        section({
            id: 'executive-summary',
            title: 'Executive Summary',
            collapsible: false,
            blocks: [
                p([
                    text('September had 166,329 streams from 69,818 streamers across 35 languages. There were 17% fewer streamers than in '),
                    link('/party-central/reports/2026-08-top-party-games-and-streamers', 'August'),
                    text('. Fewer people playing '),
                    em('MECCHA CHAMELEON'),
                    text(' and '),
                    em('The Choicer Voicer'),
                    text(' accounted for about 65% of the drop in streams.'),
                ]),
                p([
                    em('nanos world'),
                    text(' kept first place in Party Score as French roleplayers continued their characters’ stories. '),
                    em('Mario Kart World'),
                    text(' returned to second after a free course update and new racing series from major Japanese creators. '),
                    em('Mahjong Soul'),
                    text(' reached third, with tournament participants bringing larger audiences to the game.'),
                ]),
                p([
                    em('Mimic Party'),
                    text(' reached 2,576 streamers in its first full month after an August 27 launch. Spanish and German creators led the pickup, and 89% of streams were in languages other than English.'),
                ]),
                p([
                    link('https://twitch.tv/jhay', 'Jhay'),
                    text(' took first place among streamers by Party Score after five months outside the rankings. Their September subathon spanned 13 games, mostly Nintendo titles, with audiences averaging more than 200 viewers for every game.'),
                ]),
                p([
                    text('Our '),
                    link('/party-central/reports#methodology', 'methodology'),
                    text(' explains the measures and rankings used throughout this report.'),
                ]),
            ],
        }),
        section({
            id: 'party-score-rankings',
            title: 'Party Score Rankings',
            collapsible: true,
            sections: [
                section({
                    id: 'party-score-rankings__games-by-party-score',
                    title: 'Games by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Mario Kart World'),
                            text(' rose from 12th to second as 20% more streamers played it than in August. Nintendo’s '),
                            link('https://www.nintendo.com/en-gb/Support/Nintendo-Switch-2/Game-Updates/How-to-Update-Mario-Kart-World-2842542.html', 'September update'),
                            text(' added 10 classic '),
                            em('Super Mario Kart'),
                            text(' courses, two Knockout Tour routes, and Spectator Mode. Streaming increased around its September 9–10 rollout.'),
                        ]),
                        p([
                            link('https://twitch.tv/indegnasen0706', '布団ちゃんと申します'),
                            text(' began '),
                            em('Mario Kart World'),
                            text(' on September 11 and returned for three more days. '),
                            link('https://twitch.tv/hanjoudesu', 'はんじょう'),
                            text(' began their own racing series on September 15. Together with '),
                            link('https://twitch.tv/kato_junichi0817', '加藤純一うん〇ちゃん'),
                            text(', they accounted for 57% of the game’s viewership. Their audiences explain most of its increase from 18 average viewers in August to 53 in September.'),
                        ]),
                        p([
                            em('Mahjong Soul'),
                            text(' returned to the top three as the '),
                            link('https://www.youtube.com/watch?v=PqqcAIuOhxw', 'Nanashi Mahjong Soul Cup'),
                            text(' brought larger Japanese audiences to the game. '),
                            link('https://twitch.tv/fps_shaka', 'fps_shaka'),
                            text(' streamed a team meeting and the tournament, while '),
                            link('https://twitch.tv/kenki521', 'けんき'),
                            text(' played during both competition days. Nearly 40% of the game’s viewers watched those two channels. The number of people streaming it fell slightly, but its average audience doubled from August.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Party Score',
                                'Streams',
                                'Streamers',
                                'Stickiness',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'nanos world', '97', '3,379', '233', '14.5', '84'],
                                [
                                    '2',
                                    'Mario Kart World',
                                    '93',
                                    '5,543',
                                    '2,296',
                                    '2.4',
                                    '53',
                                ],
                                ['3', 'Mahjong Soul', '92', '3,332', '1,299', '2.6', '28'],
                                ['4', 'Words On Stream', '92', '6,892', '601', '11.5', '16'],
                                [
                                    '5',
                                    'Just Dance 2026 Edition',
                                    '92',
                                    '422',
                                    '163',
                                    '2.6',
                                    '68',
                                ],
                                ['6', 'Pinball', '92', '589', '141', '4.2', '29'],
                                ['7', 'Fall Guys', '91', '10,872', '4,963', '2.2', '26'],
                                [
                                    '8',
                                    'Super Smash Bros. Ultimate',
                                    '91',
                                    '11,898',
                                    '3,622',
                                    '3.3',
                                    '18',
                                ],
                                [
                                    '9',
                                    'Diablo II: Resurrected',
                                    '91',
                                    '8,656',
                                    '1,451',
                                    '6.0',
                                    '15',
                                ],
                                [
                                    '10',
                                    'Super Mario Party Jamboree',
                                    '90',
                                    '1,203',
                                    '572',
                                    '2.1',
                                    '29',
                                ],
                                ['11', 'Mario Kart 64', '90', '368', '125', '2.9', '29'],
                                ['12', 'Wortgewandt', '90', '476', '101', '4.7', '21'],
                                ['13', 'Lycans', '90', '158', '74', '2.1', '223'],
                                ['14', 'Casino', '90', '137', '47', '2.9', '215'],
                                ['15', 'Musical Vibes', '90', '401', '39', '10.3', '34'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'party-score-rankings__streamers-by-party-score',
                    title: 'Streamers by Party Score',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/jhay', 'Jhay'),
                            text(' was absent from the previous five Party Score lists and went straight to first. Their September subathon moved through 13 games, with '),
                            em('Wii Sports Resort'),
                            text(', '),
                            em('Mario Kart Wii'),
                            text(', and '),
                            em('Mario Party DS'),
                            text(' among the main stops. Every game averaged more than 200 viewers.'),
                        ]),
                        p([
                            link('https://twitch.tv/firedragon', 'FireDragon'),
                            text(' made their first appearance in the Party Score top 15. Most of their activity came from '),
                            em('Placid Plastic Duck Simulator'),
                            text(' during overnight subathon breaks, alongside '),
                            em('Super Monkey Ball 2'),
                            text(' speedruns and '),
                            em('Fall Guys'),
                            text(' rank challenges. '),
                            link('https://twitch.tv/norianne', 'Norianne'),
                            text(' also joined the list for the first time, alternating '),
                            em('Fall Guys'),
                            text(' with Wii sessions during a subathon.'),
                        ]),
                        p([
                            link('https://twitch.tv/acai', 'Acai'),
                            text(' brought a different specialty to the list: instrument challenges across five rhythm games. They attempted randomized '),
                            em('Guitar Hero III: Legends of Rock'),
                            text(' runs where a mistake ends the turn, practiced '),
                            em('Rock Band 2'),
                            text(' drum songs with the goal of an unbroken note streak, and played custom songs in '),
                            em('Guitar Hero On Tour: Modern Hits'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Party Score',
                                'Observations',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/jhay', 'Jhay')],
                                    '99',
                                    '35',
                                    '356',
                                    '13',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/nohoho12', 'のほほ')],
                                    '98',
                                    '39',
                                    '400',
                                    '3',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/mang0', 'mang0')],
                                    '98',
                                    '39',
                                    '271',
                                    '3',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/astarriapple', 'AstarriApple')],
                                    '98',
                                    '24',
                                    '179',
                                    '10',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/firedragon', 'FireDragon')],
                                    '98',
                                    '62',
                                    '158',
                                    '3',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/gamescage', 'GamesCage')],
                                    '97',
                                    '13',
                                    '499',
                                    '5',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/acai', 'Acai')],
                                    '97',
                                    '15',
                                    '439',
                                    '5',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/zeroyalviking', 'ZeRoyalViking')],
                                    '97',
                                    '12',
                                    '340',
                                    '7',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/karacorvus', 'KaraCorvus')],
                                    '97',
                                    '14',
                                    '330',
                                    '4',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '97',
                                    '15',
                                    '247',
                                    '13',
                                ],
                                [
                                    '11',
                                    [
                                        link('https://twitch.tv/francesconigris', 'francesconigris'),
                                    ],
                                    '97',
                                    '23',
                                    '159',
                                    '4',
                                ],
                                [
                                    '12',
                                    [link('https://twitch.tv/norianne', 'Norianne')],
                                    '97',
                                    '23',
                                    '153',
                                    '4',
                                ],
                                [
                                    '13',
                                    [link('https://twitch.tv/dooleynoted', 'DooleyNoted')],
                                    '96',
                                    '9',
                                    '837',
                                    '6',
                                ],
                                [
                                    '14',
                                    [link('https://twitch.tv/hypnoshark', 'hypnoshark')],
                                    '96',
                                    '10',
                                    '346',
                                    '4',
                                ],
                                [
                                    '15',
                                    [link('https://twitch.tv/vigi', 'Vigi')],
                                    '96',
                                    '10',
                                    '305',
                                    '7',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'global-overview',
            title: 'Global Overview',
            collapsible: true,
            sections: [
                section({
                    id: 'global-overview__games-with-most-streams',
                    title: 'Games with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('MECCHA CHAMELEON'),
                            text(' lost the lead in streams for the first time since June, falling behind '),
                            em('Super Smash Bros. Ultimate'),
                            text(', '),
                            em('Among Us'),
                            text(', and '),
                            em('Fall Guys'),
                            text('. Its stream count fell by more than half from August as the July rush of people trying it continued to fade.'),
                        ]),
                        p([
                            em('The Choicer Voicer'),
                            text(' dropped out of the top 10 after August’s burst of attention. Its developer '),
                            link('https://yeahmaybe.itch.io/the-choicer-voicer/devlog/1616098/answering-recent-questions', 'reported an influx of players'),
                            text(' on August 3. Only about 5% of August streamers returned in September. The wave brought plenty of people to try the game, but few became regulars.'),
                        ]),
                        p([
                            em('Diablo II: Resurrected'),
                            text(' streaming declined during September, with about 32% fewer streams in the final seven days of the month than in the first seven. Average viewers fell from 21 to 15 over the same period.'),
                        ]),
                        p([
                            em('Fall Guys'),
                            text(' owed more than 25% of its audience to '),
                            link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                            text(' in just two appearances. Without that channel, the game averaged about 19 viewers rather than 26.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'Super Smash Bros. Ultimate', '11,898', '3,622', '18'],
                                ['2', 'Among Us', '11,781', '6,999', '21'],
                                ['3', 'Fall Guys', '10,872', '4,963', '26'],
                                ['4', 'MECCHA CHAMELEON', '10,076', '7,718', '27'],
                                ['5', 'Diablo II: Resurrected', '8,656', '1,451', '15'],
                                ['6', 'Words On Stream', '6,892', '601', '16'],
                                ['7', 'Lethal Company', '6,228', '3,449', '11'],
                                ['8', 'Mario Kart World', '5,543', '2,296', '53'],
                                ['9', 'Gartic Phone', '4,306', '2,527', '18'],
                                ['10', 'Jackbox Party Packs', '3,805', '1,935', '27'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-biggest-audiences',
                    title: 'Games with Biggest Audiences',
                    description: 'Minimum 10 streams',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Beetleball'),
                            text(' launched on September 3 and led the audience ranking after visits from '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text(' and '),
                            link('https://twitch.tv/joe_bartolozzi', 'Joe_Bartolozzi'),
                            text('. Between them, they drew 80% of the game’s viewers. The other channels playing it averaged about 140 viewers.'),
                        ]),
                        p([
                            text('German game nights put '),
                            em('Hide and Moo!'),
                            text(' near the top. Players '),
                            link('https://store.steampowered.com/app/5061170/Hide_and_Moo/', 'hide among farm animals by copying their noises'),
                            text(', a setup '),
                            link('https://twitch.tv/bonjwa', 'Bonjwa'),
                            text(' brought to more than 6,500 average viewers. '),
                            link('https://twitch.tv/krokoboss', 'krokoboss'),
                            text(' and '),
                            link('https://twitch.tv/xthesolutiontv', 'xTheSolutionTV'),
                            text(' also played. Almost all of the game’s viewership came from German channels, even though English creators streamed it more often.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Game',
                                'Avg Viewers',
                                'Streams',
                                'Streamers',
                            ],
                            rows: [
                                ['1', 'Beetleball', '684', '92', '64'],
                                ['2', 'LEGO Party!', '375', '138', '115'],
                                ['3', 'Hide and Moo!', '297', '140', '129'],
                                ['4', 'Slots Royale: 777 Casino Games', '265', '31', '8'],
                                [
                                    '5',
                                    'Super Mario Party Jamboree: Nintendo Switch 2 Edition + Jamboree TV',
                                    '247',
                                    '99',
                                    '54',
                                ],
                                ['6', 'Lycans', '223', '158', '74'],
                                ['7', 'Casino', '215', '137', '47'],
                                ['8', 'Jinsei Game for Nintendo Switch', '186', '12', '11'],
                                ['9', 'Gaggl', '144', '160', '104'],
                                ['10', 'Family Feud', '134', '76', '72'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-streamed-most-widely',
                    title: 'Games Streamed Most Widely',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Mimic Party'),
                            text(' spread through groups of friends and streams that invited viewers to join. Spanish and German had the '),
                            link('/party-central?visualization=bar&measure=uniqueStreamerCount&x=language&grain=&from=2026-09-01T00%3A00%3A00.000Z&to=2026-10-01T00%3A00%3A00.000Z&game=1544653089', 'largest groups of streamers playing it'),
                            text('. '),
                            link('https://twitch.tv/eneninn', 'Eneninn'),
                            text(' and '),
                            link('https://twitch.tv/termiigod', 'Termiigod'),
                            text(' named the same friends in their stream titles, while '),
                            link('https://twitch.tv/coachlim', 'coachlim'),
                            text(' invited viewers to play. Its '),
                            link('https://store.steampowered.com/app/5053820/Mimic_Party/', 'sound-imitation rounds and custom sound packs'),
                            text(' give groups room to use their own jokes and familiar noises.'),
                        ]),
                        p([
                            text('Polish-language '),
                            em('Mimic Party'),
                            text(' streams averaged 355 viewers. Most of that audience watched '),
                            link('https://twitch.tv/youngmulti', 'YoungMulti'),
                            text(' play with their crew: one stream drew 84% of the game’s Polish viewers.'),
                        ]),
                        p([
                            em('MECCHA CHAMELEON'),
                            text(' still reached more streamers than any other game, but only 11% of its August streamers returned in September.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Streamers', 'Streams'],
                            rows: [
                                ['1', 'MECCHA CHAMELEON', '7,718', '10,076'],
                                ['2', 'Among Us', '6,999', '11,781'],
                                ['3', 'Fall Guys', '4,963', '10,872'],
                                ['4', 'Super Smash Bros. Ultimate', '3,622', '11,898'],
                                ['5', 'Lethal Company', '3,449', '6,228'],
                                ['6', 'Mimic Party', '2,576', '3,149'],
                                ['7', 'Gartic Phone', '2,527', '4,306'],
                                ['8', 'Mario Kart World', '2,296', '5,543'],
                                ['9', 'Jackbox Party Packs', '1,935', '3,805'],
                                ['10', 'Super Battle Golf', '1,903', '2,428'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'global-overview__games-with-stickiest-streamers',
                    title: 'Games with Stickiest Streamers',
                    description: 'Minimum 20 unique streamers',
                    collapsible: true,
                    blocks: [
                        p([
                            em('nanos world'),
                            text(' kept its lead through returning roleplayers. Creators who also played in August were behind 79% of September’s streams. The busiest channel made up just 4% of the total, showing broad dedication to the game.'),
                        ]),
                        p([
                            text('Eleven '),
                            em('Words On Stream'),
                            text(' channels were online almost every time we checked. Together, they produced 31% of the game’s streams. About half of its streamers appeared only once, so the regular channels explain much of its high stickiness.'),
                        ]),
                        p([
                            em('Scrabble'),
                            text(' owes most of its repeat activity to '),
                            link('https://twitch.tv/studycade', 'StudyCade'),
                            text(', whose chat-based word practice filled 79% of the game’s streams. The other 19 channels averaged about two streams each.'),
                        ]),
                        p([
                            em('Placid Plastic Duck Simulator'),
                            text(' served as a quiet backdrop for long broadcasts. '),
                            link('https://twitch.tv/david_demantoid', 'David_Demantoid'),
                            text(' used it during a fifth-anniversary donation marathon, and several smaller channels ran duck-and-music streams through most of the month.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Rank', 'Game', 'Stickiness', 'Streams', 'Streamers'],
                            rows: [
                                ['1', 'nanos world', '14.5', '3,379', '233'],
                                ['2', 'Words On Stream', '11.5', '6,892', '601'],
                                ['3', 'Musical Vibes', '10.3', '401', '39'],
                                ['4', 'Drawful 2', '10.0', '229', '23'],
                                ['5', 'Scrabble', '8.8', '177', '20'],
                                ['6', 'Ragnarok Origin: Classic', '8.2', '730', '89'],
                                ['7', 'Dragon Warrior IV', '7.5', '218', '29'],
                                ['8', 'Placid Plastic Duck Simulator', '7.0', '1,658', '236'],
                                [
                                    '9',
                                    'The Legend of Heroes: Trails of Cold Steel III',
                                    '6.9',
                                    '233',
                                    '34',
                                ],
                                ['10', 'Fruit Mountain', '6.8', '142', '21'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'new-releases',
            title: 'New Releases',
            collapsible: true,
            sections: [
                section({
                    id: 'new-releases__new-releases-seen-on-twitch',
                    title: 'New Releases Seen on Twitch',
                    collapsible: true,
                    blocks: [
                        p([
                            em('Shady Job'),
                            text(' reached the most streamers among September’s releases. In this '),
                            link('https://store.steampowered.com/app/4190710/Shady_job/', 'cooperative construction game'),
                            text(', workers carry a body bag up a skyscraper while its occupant struggles and damages the building. English and Russian creators did most of the streaming. The biggest audiences watched German channels, led by '),
                            link('https://twitch.tv/trymacs', 'Trymacs'),
                            text(', whose brief visit drew nearly half of the game’s viewers.'),
                        ]),
                        p([
                            em('Beetleball'),
                            text(' brought dung-beetle soccer to 64 channels after its '),
                            link('https://store.steampowered.com/app/2776270/Beetleball/', 'September 3 release'),
                            text('. Most played it only briefly, but appearances by two well-known creators gave it a much higher average audience than the other new releases.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Game',
                                'Released',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['Shady Job', 'Sep 10', '186', '163', '124'],
                                ['Masters of Mayhem', 'Sep 1', '134', '77', '7'],
                                ['Beetleball', 'Sep 3', '92', '64', '684'],
                                [
                                    'Night Club Simulator: Party King',
                                    'Sep 16',
                                    '70',
                                    '58',
                                    '47',
                                ],
                                ['Phobos', 'Sep 16', '61', '50', '25'],
                                ['Ride Up Together', 'Sep 16', '24', '23', '32'],
                                ['Bug Cleaners', 'Sep 28', '22', '20', '6'],
                                ['Data Breach', 'Sep 4', '5', '5', '9'],
                                ['Pop Float a-way', 'Sep 1', '2', '2', '12'],
                                ['Hyperboard Extreme', 'Sep 26', '1', '1', '8'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'new-releases__other-new-releases',
                    title: 'Other New Releases',
                    collapsible: true,
                    blocks: [
                        p('Our tracker did not record Twitch activity for 34 other party games released in September. They are listed below by release date.'),
                        list({
                            items: [
                                'BlameChat (Sep 1)',
                                'Alluring Neon Night Town (Sep 3)',
                                'Caught You Looking (Sep 3)',
                                'Kapka The Game (Sep 3)',
                                'Moo-A-Doodle-Doo (Sep 3)',
                                'Secret Photo Memory (Sep 3)',
                                'Street Corner Heartbeat Snaps (Sep 3)',
                                'Betrayal Banquet (Sep 10)',
                                'Door Runners (Sep 10)',
                                'Flipi Rivals (Sep 10)',
                                'Super Takoyaki Battle (Sep 10)',
                                '100 Romantic Hidden Cat (Sep 11)',
                                'Dwarf Core (Sep 11)',
                                'Chit Chat Party! (Sep 16)',
                                'Dub Rumble (Sep 17)',
                                'Granblue Fantasy Versus: Rising - Legendary Edition (Sep 17)',
                                'Psychopath Mind Quiz: Unmask Their Dark Nature (Sep 17)',
                                'That Cloud Looks Like (Sep 19)',
                                'Beautiful Girls on Campus: Memories of Youth (Sep 24)',
                                'Floor Wiping Race (Sep 24)',
                                "Hack 'n' Stack (Sep 24)",
                                'Phantom Thief Mirage and the Curious Clues (Sep 24)',
                                'Tiny Tank Showdown (Sep 24)',
                                'What Was Here? 1-Minute Memory Quiz!! (Sep 24)',
                                'Abandoned Gameshop (Sep 25)',
                                'Neighbors: Suburban Warfare (Sep 29)',
                                'Crewed (Sep 30)',
                                'Dark Bros (Sep 30)',
                                'DungeonBox (Sep 30)',
                                'Fatal Train (Sep 30)',
                                'Redhowl (Sep 30)',
                                'Stinker Golf (Sep 30)',
                                'Teddy Is Coming (Sep 30)',
                                'The 4 Masked (Sep 30)',
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streamer-spotlight',
            title: 'Streamer Spotlight',
            collapsible: true,
            sections: [
                section({
                    id: 'streamer-spotlight__streamers-with-most-activity',
                    title: 'Streamers with Most Activity',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/yoonessa', 'YooNessa'),
                            text(' reached the top 10 most active streamers thanks to their subathon that ran throughout September. They were the only creator in the top 10 Streamers with Most Activity to play more than one game in this report, although '),
                            em('Wortgewandt'),
                            text(' accounted for almost all of their activity. They took brief breaks from it to play '),
                            em('Duel: The Game Show'),
                            text(', '),
                            em('First Class Trouble'),
                            text(', and '),
                            em('Mimic Party'),
                            text('.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Observations',
                                'Avg Viewers',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/telinha', 'Telinha')],
                                    '203',
                                    '116',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate')],
                                    '203',
                                    '20',
                                    '1',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                    '203',
                                    '10',
                                    '1',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                    '202',
                                    '85',
                                    '1',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                    '199',
                                    '31',
                                    '1',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                    '198',
                                    '22',
                                    '1',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/luckdoggz', 'Luckdoggz')],
                                    '158',
                                    '53',
                                    '1',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/toxeeec', 'toxeeec')],
                                    '139',
                                    '93',
                                    '1',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/yoonessa', 'YooNessa')],
                                    '94',
                                    '16',
                                    '4',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/shadowwgkd', 'ShadowwGKD')],
                                    '88',
                                    '109',
                                    '1',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-biggest-audiences',
                    title: 'Streamers with Biggest Audiences',
                    description: 'Minimum 2 observations',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                            text(' topped the list through two '),
                            em('Fall Guys'),
                            text(' appearances, averaging nearly 39,000 viewers. '),
                            link('https://twitch.tv/dangerlyoha', 'dangerlyoha'),
                            text(' debuted in the audience top 10 by playing '),
                            em('Mimic Party'),
                            text(' with other creators on September 3 and 7.'),
                        ]),
                        p([
                            link('https://twitch.tv/ironmouse', 'ironmouse'),
                            text(' returned to the top 10 after their May appearance, playing voice-impression and cooperative puzzle games during their Mouseathon. '),
                            em('The Choicer Voicer'),
                            text(' appeared on September 3, and '),
                            link('https://totalmayhemgames.com/games/the-friendship', [
                                em('We Were Here Expeditions: The FriendShip'),
                            ]),
                            text(' on September 30. Both drew around 10,500 viewers.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Avg Viewers',
                                'Observations',
                                'Games',
                            ],
                            rows: [
                                [
                                    '1',
                                    [
                                        link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                                    ],
                                    '38,816',
                                    '2',
                                    '1',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                    '34,006',
                                    '2',
                                    '2',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/tumblurr', 'Tumblurr')],
                                    '31,821',
                                    '3',
                                    '3',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/papaplatte', 'Papaplatte')],
                                    '23,622',
                                    '2',
                                    '2',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/batora324', 'バトラ')],
                                    '17,098',
                                    '2',
                                    '2',
                                ],
                                [
                                    '6',
                                    [link('https://twitch.tv/fps_shaka', 'fps_shaka')],
                                    '16,905',
                                    '4',
                                    '2',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/dangerlyoha', 'dangerlyoha')],
                                    '16,058',
                                    '2',
                                    '1',
                                ],
                                [
                                    '8',
                                    [
                                        link('https://twitch.tv/indegnasen0706', '布団ちゃんと申します'),
                                    ],
                                    '15,524',
                                    '4',
                                    '1',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/trymacs', 'Trymacs')],
                                    '14,039',
                                    '2',
                                    '2',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/ironmouse', 'ironmouse')],
                                    '10,608',
                                    '2',
                                    '2',
                                ],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streamer-spotlight__streamers-with-most-versatility',
                    title: 'Streamers with Most Versatility',
                    description: 'Minimum 10 average viewers',
                    collapsible: true,
                    blocks: [
                        p([
                            link('https://twitch.tv/fuzzyness', 'Fuzzyness'),
                            text(' played 13 '),
                            em('Mario Party'),
                            text(' games in a five-day all-boards marathon. The tour ran from the original game to '),
                            em('Super Mario Party Jamboree'),
                            text(', with handheld entries such as '),
                            em('Mario Party Advance'),
                            text(' and '),
                            em('Mario Party DS'),
                            text(' along the way. This was their debut on the versatility list.'),
                        ]),
                        p([
                            link('https://twitch.tv/dead_4life', 'Dead_4Life'),
                            text(' was also new to the list, mixing record attempts with community racing during a subathon. '),
                            em('Mario Party 6'),
                            text(' all-boards attempts were the most frequent stop. '),
                            em('Mario Kart DS'),
                            text(' speedruns and a 96-track '),
                            em('Mario Kart 8 Deluxe'),
                            text(' community race helped broaden their month from five games in August to 11.'),
                        ]),
                        p([
                            link('https://twitch.tv/jhay', 'Jhay'),
                            text(' moved beyond the '),
                            em('Mario Party'),
                            text(' and '),
                            em('Mario Kart'),
                            text(' series during their subathon. '),
                            em('Barnyard'),
                            text(', '),
                            em('Just Dance 4'),
                            text(', and '),
                            em('Jackbox Party Packs'),
                            text(' joined the Nintendo rotation. On September 2 alone, the stream switched between four different games.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Streamer',
                                'Games',
                                'Observations',
                                'Avg Viewers',
                            ],
                            rows: [
                                [
                                    '1',
                                    [link('https://twitch.tv/partychads', 'PARTYCHADS')],
                                    '16',
                                    '61',
                                    '17',
                                ],
                                [
                                    '2',
                                    [link('https://twitch.tv/jhay', 'Jhay')],
                                    '13',
                                    '35',
                                    '356',
                                ],
                                [
                                    '3',
                                    [link('https://twitch.tv/fuzzyness', 'Fuzzyness')],
                                    '13',
                                    '17',
                                    '87',
                                ],
                                [
                                    '4',
                                    [link('https://twitch.tv/pietsmiettv', 'PietSmietTV')],
                                    '13',
                                    '15',
                                    '247',
                                ],
                                [
                                    '5',
                                    [link('https://twitch.tv/dead_4life', 'Dead_4Life')],
                                    '11',
                                    '29',
                                    '45',
                                ],
                                [
                                    '6',
                                    [
                                        link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                    ],
                                    '10',
                                    '68',
                                    '36',
                                ],
                                [
                                    '7',
                                    [link('https://twitch.tv/astarriapple', 'AstarriApple')],
                                    '10',
                                    '24',
                                    '179',
                                ],
                                [
                                    '8',
                                    [link('https://twitch.tv/baebykk', 'baebyKK')],
                                    '10',
                                    '10',
                                    '27',
                                ],
                                [
                                    '9',
                                    [link('https://twitch.tv/quarrysea', 'QuarrySea')],
                                    '8',
                                    '24',
                                    '58',
                                ],
                                [
                                    '10',
                                    [link('https://twitch.tv/andojeebo', 'AndoJeebo')],
                                    '7',
                                    '27',
                                    '17',
                                ],
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'language-landscape',
            title: 'Language Landscape',
            collapsible: true,
            sections: [
                section({
                    id: 'language-landscape__languages-with-most-streams',
                    title: 'Languages with Most Streams',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Russian had the largest relative drop-off among the most-streamed languages; however, English lost the most absolute streamers in September, with nearly 10,000 fewer people playing party games month over month. About 63% of that fall came from fewer people streaming only '),
                            em('MECCHA CHAMELEON'),
                            text(' or '),
                            em('The Choicer Voicer'),
                            text('. Other party games had 9% fewer English-speaking streamers, too.'),
                        ]),
                        p([
                            text('Japanese streams averaged 53 viewers, up from 20 in August. About 75% of the growth in Japanese viewership came from '),
                            em('Mario Kart World'),
                            text('. '),
                            link('https://twitch.tv/indegnasen0706', '布団ちゃんと申します'),
                            text(' and '),
                            link('https://twitch.tv/hanjoudesu', 'はんじょう'),
                            text(' began racing series that brought large audiences to the game.'),
                        ]),
                        p([
                            text('French streamers played '),
                            em('nanos world'),
                            text(' '),
                            link('/party-central?visualization=bar&x=game&grain=&from=2026-09-01T00%3A00%3A00.000Z&to=2026-10-01T00%3A00%3A00.000Z&language=fr', 'nearly four times as often'),
                            text(' as the second-most-streamed game, '),
                            em('Among Us'),
                            text('. No other major language group had such a wide gap between its first two games. Half of all French viewership came from watching '),
                            em('nanos world'),
                            text(' streams, helping keep the language’s average audience high. '),
                            link('https://twitch.tv/mehditdonc', 'MehDitDonc'),
                            text(' and '),
                            link('https://twitch.tv/tayoh_', 'Tayoh_'),
                            text(' continued their characters’ stories on Zenkai, an established '),
                            link('https://docs.nanos-world.com/blog/march-2026', 'French ninja roleplay server'),
                            text('. MehDitDonc’s Ryūji Uchiha storyline began in August and reached Episode 43 by the end of September.'),
                        ]),
                        p([
                            text('German audiences were smaller than in August, mainly because '),
                            em('Among Us'),
                            text(' had fewer viewers. That game accounted for about 78% of the fall in German viewership. '),
                            link('https://twitch.tv/eliasn97', 'eliasn97'),
                            text(' still led the German audience ranking, despite smaller crowds.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: [
                                'Rank',
                                'Language',
                                'Streams',
                                'Streamers',
                                'Avg Viewers',
                            ],
                            rows: [
                                ['1', 'English', '98,150', '39,520', '17'],
                                ['2', 'Spanish', '15,432', '7,521', '13'],
                                ['3', 'French', '11,086', '4,057', '50'],
                                ['4', 'German', '10,214', '4,740', '44'],
                                ['5', 'Japanese', '9,088', '3,753', '53'],
                                ['6', 'Russian', '7,299', '3,362', '27'],
                                ['7', 'Portuguese', '6,116', '2,619', '23'],
                                ['8', 'Chinese', '2,527', '1,089', '29'],
                                ['9', 'Italian', '1,373', '707', '112'],
                                ['10', 'Polish', '1,048', '486', '46'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__english',
                    title: 'English',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__english__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Super Smash Bros. Ultimate', '7,925', '2,330'],
                                        ['2', 'Fall Guys', '6,025', '2,799'],
                                        ['3', 'Among Us', '5,992', '3,587'],
                                        ['4', 'Words On Stream', '5,905', '511'],
                                        ['5', 'Diablo II: Resurrected', '5,416', '892'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'The Beatles: Rock Band', '820', '6'],
                                        ['2', 'Beetleball', '264', '84'],
                                        [
                                            '3',
                                            'SpongeBob SquarePants: Lights, Camera, Pants!',
                                            '243',
                                            '9',
                                        ],
                                        ['4', 'Slots Royale: 777 Casino Games', '239', '13'],
                                        ['5', 'BBQ Simulator: The Squad', '229', '5'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '203',
                                            '20',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '203',
                                            '10',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/mrpeanut1028', 'MrPeanut1028'),
                                            ],
                                            '203',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '202',
                                            '85',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '199',
                                            '31',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__english__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                                            ],
                                            '38,816',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/ironmouse', 'ironmouse')],
                                            '10,608',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/northernlion', 'Northernlion'),
                                            ],
                                            '9,758',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/valkyrae', 'Valkyrae')],
                                            '6,008',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/erobb221', 'erobb221')],
                                            '3,469',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__spanish',
                    title: 'Spanish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__spanish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '1,960', '1,144'],
                                        ['2', 'Super Smash Bros. Ultimate', '1,854', '596'],
                                        ['3', 'Fall Guys', '1,565', '850'],
                                        ['4', 'Mario Kart World', '904', '329'],
                                        ['5', 'Gartic Phone', '848', '510'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Culdcept Begins', '163', '5'],
                                        ['2', 'Wii Sports', '126', '17'],
                                        ['3', 'Mimic Party', '96', '837'],
                                        ['4', 'Just Dance 2026 Edition', '48', '61'],
                                        [
                                            '5',
                                            'Super Mario Party Jamboree: Nintendo Switch 2 Edition + Jamboree TV',
                                            '34',
                                            '14',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/pibe_lgsuz', 'pibe_lgsuz')],
                                            '43',
                                            '18',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/vizard39', 'vizard39')],
                                            '38',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/katzefun', 'KatzeFun')],
                                            '38',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/shy_guay', 'shy_guay')],
                                            '34',
                                            '10',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/exponso', 'Exponso')],
                                            '28',
                                            '37',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__spanish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/paache_', 'paache_')],
                                            '2,245',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/13steg', '13steg')],
                                            '645',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/stephaniemx_', 'stephaniemx_'),
                                            ],
                                            '421',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/purrjectyui', 'purrjectyui')],
                                            '352',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/eneninn', 'Eneninn')],
                                            '312',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__french',
                    title: 'French',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__french__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'nanos world', '3,177', '221'],
                                        ['2', 'Among Us', '837', '492'],
                                        ['3', 'Fall Guys', '808', '347'],
                                        ['4', 'Super Smash Bros. Ultimate', '706', '251'],
                                        ['5', 'Mario Kart World', '686', '269'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'LEGO Party!', '2,957', '11'],
                                        ['2', 'Deep Blue Sushi', '1,537', '7'],
                                        ['3', 'Maitre des Fleurs', '482', '8'],
                                        ['4', 'The Choicer Voicer', '331', '34'],
                                        ['5', 'Super Mario Party Jamboree', '310', '48'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/anas6424v1', 'anas6424v1')],
                                            '201',
                                            '6',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/shadowwgkd', 'ShadowwGKD')],
                                            '88',
                                            '109',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/walidoen17', 'walidoen17')],
                                            '68',
                                            '72',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kyle_offf', 'kyle_offf')],
                                            '67',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/crashkill', 'CrashKill')],
                                            '66',
                                            '89',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__french__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/etoiles', 'Etoiles')],
                                            '5,606',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/gom4rt', 'Gom4rt')],
                                            '3,968',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mynthos', 'Mynthos')],
                                            '3,716',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/ponce', 'Ponce')],
                                            '3,498',
                                            '7',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '2,759',
                                            '40',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__german',
                    title: 'German',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__german__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Among Us', '1,378', '747'],
                                        ['2', 'MECCHA CHAMELEON', '1,069', '803'],
                                        ['3', 'Diablo II: Resurrected', '941', '111'],
                                        ['4', 'Fall Guys', '649', '337'],
                                        ['5', 'Mimic Party', '587', '444'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Hide and Moo!', '1,027', '39'],
                                        ['2', 'Shady Job', '768', '20'],
                                        ['3', 'UNO', '612', '22'],
                                        ['4', 'Nintendo Switch Sports', '330', '6'],
                                        ['5', 'Just Dance 2026 Edition', '230', '30'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/yoonessa', 'YooNessa')],
                                            '94',
                                            '16',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/xthesolutionvod', 'xTheSolutionVOD'),
                                            ],
                                            '68',
                                            '36',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/scavy91', 'Scavy91')],
                                            '55',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/das__mufflon', 'Das__Mufflon'),
                                            ],
                                            '50',
                                            '44',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/zan_koko', 'zan_koko')],
                                            '44',
                                            '23',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__german__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/eliasn97', 'eliasn97')],
                                            '34,006',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/papaplatte', 'Papaplatte')],
                                            '23,622',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/trymacs', 'Trymacs')],
                                            '14,039',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/bonjwa', 'Bonjwa')],
                                            '6,518',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/xrohat', 'xRohat')],
                                            '6,368',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__japanese',
                    title: 'Japanese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__japanese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '1,853', '673'],
                                        ['2', 'MECCHA CHAMELEON', '988', '797'],
                                        ['3', 'Super Smash Bros. Ultimate', '782', '210'],
                                        ['4', 'Beat Saber', '602', '132'],
                                        ['5', 'Mario Kart World', '463', '151'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Mario Kart World', '460', '463'],
                                        ['2', 'Jinsei Game for Nintendo Switch', '244', '9'],
                                        ['3', 'Super Battle Golf', '99', '38'],
                                        ['4', 'Super Smash Bros. Ultimate', '82', '782'],
                                        [
                                            '5',
                                            'We Love Katamari REROLL+ Royal Reverie',
                                            '62',
                                            '19',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/kept_varrel', 'kept_VARREL')],
                                            '53',
                                            '153',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/makosu', 'makosu')],
                                            '50',
                                            '18',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/bikzooo', 'びくぞー')],
                                            '41',
                                            '14',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/pokapokacan', 'ぽかぽかさん')],
                                            '40',
                                            '33',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/hika_tin115', 'ひかちゃんねる'),
                                            ],
                                            '40',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__japanese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/batora324', 'バトラ')],
                                            '17,098',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/fps_shaka', 'fps_shaka')],
                                            '16,905',
                                            '4',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/indegnasen0706', '布団ちゃんと申します'),
                                            ],
                                            '15,524',
                                            '4',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/yaritaiji', 'たいじ_たいちゃんねる'),
                                            ],
                                            '8,906',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kenki521', 'けんき')],
                                            '2,853',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__russian',
                    title: 'Russian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__russian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Gartic Phone', '1,129', '592'],
                                        ['2', 'Diablo II: Resurrected', '762', '132'],
                                        ['3', 'Jackbox Party Packs', '700', '237'],
                                        ['4', 'MECCHA CHAMELEON', '675', '511'],
                                        ['5', 'SIGame', '462', '303'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Casino', '347', '52'],
                                        ['2', 'Mimic Party', '284', '216'],
                                        ['3', 'Shady Job', '127', '50'],
                                        ['4', 'The Choicer Voicer', '83', '30'],
                                        ['5', 'Gartic Show', '63', '72'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/grendelin', 'Grendelin')],
                                            '44',
                                            '16',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/hc_fm', 'hc_fm')],
                                            '43',
                                            '14',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/demonyash_', 'Demonyash_')],
                                            '39',
                                            '14',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/vangavgav', 'vangavgav')],
                                            '32',
                                            '23',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kypced', 'kypced')],
                                            '30',
                                            '138',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__russian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/dangerlyoha', 'dangerlyoha')],
                                            '16,058',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mazellovvv', 'mazellovvv')],
                                            '4,622',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/orkpod', 'orkpod')],
                                            '3,469',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/alfedov', 'Alfedov')],
                                            '794',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/trxstren', 'trxstren')],
                                            '651',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__portuguese',
                    title: 'Portuguese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__portuguese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '603', '171'],
                                        ['2', 'Ragnarok Origin: Classic', '601', '65'],
                                        ['3', 'Among Us', '451', '254'],
                                        ['4', 'MECCHA CHAMELEON', '430', '173'],
                                        ['5', 'Words On Stream', '404', '49'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Slots Royale: 777 Casino Games', '465', '11'],
                                        ['2', 'Keep Talking and Nobody Explodes', '336', '5'],
                                        ['3', 'Lycans', '203', '6'],
                                        ['4', 'Mimic Party', '99', '227'],
                                        ['5', 'Pummel Party', '93', '126'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '203',
                                            '116',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/scdzeus', 'scdzeus')],
                                            '58',
                                            '13',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/cesanasz', 'Cesanasz')],
                                            '54',
                                            '13',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/jotta', 'jotta')],
                                            '53',
                                            '204',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/phwasd', 'phwasd')],
                                            '51',
                                            '8',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__portuguese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/suetam1v4', 'suetam1v4')],
                                            '1,569',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/boltz', 'boltz')],
                                            '1,498',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/wanessawolf', 'wanessawolf')],
                                            '1,335',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/heitir', 'Heitir')],
                                            '766',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/felps', 'Felps')],
                                            '758',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__chinese',
                    title: 'Chinese',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__chinese__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Mahjong Soul', '515', '248'],
                                        ['2', 'Diablo II: Resurrected', '288', '35'],
                                        ['3', 'Mabinogi', '211', '40'],
                                        ['4', 'MECCHA CHAMELEON', '202', '128'],
                                        ['5', 'Party Animals', '166', '87'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Galumb', '1,999', '9'],
                                        ['2', 'KLETKA', '515', '8'],
                                        ['3', 'Yakiniku Simulatior', '392', '5'],
                                        ['4', 'POPUCOM', '255', '7'],
                                        ['5', 'Mimic Party', '74', '84'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/andyyeh88', '阿棠1214')],
                                            '46',
                                            '14',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/aldia1204', '艾爾狄亞')],
                                            '36',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/x0202447', '千堂兒')],
                                            '30',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/maxdeng', '新八姬')],
                                            '26',
                                            '9',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/vs22222', 'vs22222')],
                                            '23',
                                            '17',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__chinese__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/godjj', '接接')],
                                            '2,901',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/dingding248', '丁義')],
                                            '1,196',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mrlo_tw', 'MrLo_tw')],
                                            '476',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/0_0tjh', '米朵粒小姐姐')],
                                            '200',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lingmu_ch', '鈴姆')],
                                            '191',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__italian',
                    title: 'Italian',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__italian__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Fall Guys', '251', '103'],
                                        ['2', 'Among Us', '195', '127'],
                                        ['3', 'Super Smash Bros. Ultimate', '73', '21'],
                                        ['4', 'Gartic Phone', '60', '48'],
                                        ['5', 'Mario Kart 8 Deluxe', '57', '22'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Golf With Your Friends', '787', '53'],
                                        ['2', 'Jackbox Party Packs', '248', '18'],
                                        ['3', 'The Choicer Voicer', '228', '39'],
                                        ['4', 'Fall Guys', '163', '251'],
                                        ['5', 'Pou', '81', '7'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/francesconigris', 'francesconigris'),
                                            ],
                                            '23',
                                            '159',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/xraizo72', 'xRaizo72')],
                                            '23',
                                            '31',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/pantofole94', 'pantofole94')],
                                            '20',
                                            '10',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/junehh_whitestar', 'junehh_whitestar'),
                                            ],
                                            '19',
                                            '14',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/j_papu_03', 'J_Papu_03')],
                                            '15',
                                            '36',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__italian__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/tumblurr', 'Tumblurr')],
                                            '31,821',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/therealmarzaa', 'TheRealMarzaa'),
                                            ],
                                            '4,925',
                                            '6',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/poldo', 'Poldo')],
                                            '614',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/pesh', 'Pesh')],
                                            '598',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/kasumisen', 'KasumiSen')],
                                            '474',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'language-landscape__polish',
                    title: 'Polish',
                    collapsible: true,
                    sections: [
                        section({
                            id: 'language-landscape__polish__games-with-most-streams',
                            title: 'Games with Most Streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Streams', 'Streamers'],
                                    rows: [
                                        ['1', 'Diablo II: Resurrected', '206', '48'],
                                        ['2', 'Fall Guys', '120', '35'],
                                        ['3', 'Lethal Company', '113', '36'],
                                        ['4', 'Among Us', '100', '54'],
                                        ['5', 'MECCHA CHAMELEON', '86', '71'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__games-with-biggest-audiences',
                            title: 'Games with Biggest Audiences',
                            description: 'Minimum 5 streams',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: ['Rank', 'Game', 'Avg Viewers', 'Streams'],
                                    rows: [
                                        ['1', 'Mimic Party', '355', '55'],
                                        ['2', 'Pummel Party', '123', '17'],
                                        ['3', 'Stumble Guys', '104', '5'],
                                        ['4', 'MECCHA CHAMELEON', '89', '86'],
                                        ['5', 'Among Us', '87', '100'],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/iergo', 'iErGO')],
                                            '27',
                                            '8',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/lucy_sally', 'Lucy_Sally')],
                                            '20',
                                            '24',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/lisasileska', 'LisaSileska')],
                                            '17',
                                            '12',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/datensheeva', 'datensheeVA')],
                                            '12',
                                            '16',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/destri566', 'destri566')],
                                            '11',
                                            '14',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'language-landscape__polish__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/nexe_', 'Nexe_')],
                                            '2,182',
                                            '3',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/xweza', 'xWeza')],
                                            '289',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/smayro', 'Smayro')],
                                            '238',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/gramypomagamy', 'GramyPomagamy'),
                                            ],
                                            '203',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/nadinwins', 'NadinWins')],
                                            '186',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'game-deep-dives',
            title: 'Game Deep Dives',
            collapsible: true,
            sections: [
                section({
                    id: 'game-deep-dives__nanos-world',
                    title: 'nanos world',
                    collapsible: true,
                    blocks: [
                        p('3,379 streams | 233 streamers | 14.5 stickiness | 84 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/shadowwgkd', 'ShadowwGKD')],
                                            '88',
                                            '109',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/walidoen17', 'walidoen17')],
                                            '68',
                                            '72',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kyle_offf', 'kyle_offf')],
                                            '67',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/crashkill', 'CrashKill')],
                                            '66',
                                            '89',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/heaven_et_alex', 'heaven_et_alex'),
                                            ],
                                            '66',
                                            '18',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__nanos-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mehditdonc', 'MehDitDonc')],
                                            '2,759',
                                            '40',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/tayoh_', 'Tayoh_')],
                                            '1,023',
                                            '31',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/khadim', 'Khadim')],
                                            '509',
                                            '14',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mowraa', 'Mowraa')],
                                            '445',
                                            '31',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/betr0uni', 'Betr0uni')],
                                            '413',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mario-kart-world',
                    title: 'Mario Kart World',
                    collapsible: true,
                    blocks: [
                        p('5,543 streams | 2,296 streamers | 2.4 stickiness | 53 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mario-kart-world__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/vantox_', 'Vantox_')],
                                            '85',
                                            '48',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/bikzooo', 'びくぞー')],
                                            '41',
                                            '14',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/tomka02', 'TOMKA02')],
                                            '33',
                                            '15',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/shy_guay', 'shy_guay')],
                                            '24',
                                            '11',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/aketxz', 'Aketxz')],
                                            '23',
                                            '24',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mario-kart-world__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/indegnasen0706', '布団ちゃんと申します'),
                                            ],
                                            '15,524',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/yaritaiji', 'たいじ_たいちゃんねる'),
                                            ],
                                            '8,906',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/hanjoudesu', 'はんじょう')],
                                            '2,826',
                                            '20',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/assarisyoko1', 'あっさりしょこー'),
                                            ],
                                            '2,594',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/os_new', 'にゅうだよ')],
                                            '1,056',
                                            '4',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__mahjong-soul',
                    title: 'Mahjong Soul',
                    collapsible: true,
                    blocks: [
                        p('3,332 streams | 1,299 streamers | 2.6 stickiness | 28 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__mahjong-soul__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/xsinstream222', 'xsinstream222'),
                                            ],
                                            '45',
                                            '31',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/hika_tin115', 'ひかちゃんねる'),
                                            ],
                                            '36',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/x0202447', '千堂兒')],
                                            '30',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/chinochimu', '延滞料金')],
                                            '27',
                                            '16',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/zenzennac', 'ぜん善')],
                                            '26',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__mahjong-soul__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/fps_shaka', 'fps_shaka')],
                                            '13,366',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kenki521', 'けんき')],
                                            '2,853',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kokujintv', 'kokujintv')],
                                            '1,016',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/mother3rd', 'まざー3')],
                                            '746',
                                            '4',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/lovelymomo', 'lovelymomo')],
                                            '618',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__words-on-stream',
                    title: 'Words On Stream',
                    collapsible: true,
                    blocks: [
                        p('6,892 streams | 601 streamers | 11.5 stickiness | 16 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/suzyqpid', 'suzyqpid')],
                                            '203',
                                            '10',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/thesptwitch', 'TheSpTwitch')],
                                            '199',
                                            '31',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/tygerladi', 'TygerLadi')],
                                            '198',
                                            '22',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/luckdoggz', 'Luckdoggz')],
                                            '158',
                                            '53',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/the_coop_couchcrew', 'the_coop_couchcrew'),
                                            ],
                                            '151',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__words-on-stream__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/taruh', 'taruh')],
                                            '505',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/antedeguemonn', 'Antedeguemonn'),
                                            ],
                                            '230',
                                            '3',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/skinnedteen', 'skinnedteen')],
                                            '222',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/bexed', 'beXed')],
                                            '213',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/jotta', 'jotta')],
                                            '201',
                                            '51',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__just-dance-2026-edition',
                    title: 'Just Dance 2026 Edition',
                    collapsible: true,
                    blocks: [
                        p('422 streams | 163 streamers | 2.6 stickiness | 68 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__just-dance-2026-edition__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/junehh_whitestar', 'junehh_whitestar'),
                                            ],
                                            '19',
                                            '14',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/sierravampire', 'SierraVampire'),
                                            ],
                                            '17',
                                            '72',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/remeladegatos', 'remeladegatos'),
                                            ],
                                            '17',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/ratatoscr', 'RATATOscr')],
                                            '12',
                                            '29',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/itsmalikopg', 'itsMalikopg')],
                                            '10',
                                            '7',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__just-dance-2026-edition__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/bebahan', 'Bebahan')],
                                            '1,408',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/sincerelylyn', 'sincerelyLyn'),
                                            ],
                                            '1,307',
                                            '5',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/sophelyy', 'Sophelyy')],
                                            '1,134',
                                            '5',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kasumisen', 'KasumiSen')],
                                            '474',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/summerskin90', 'summerskin90'),
                                            ],
                                            '312',
                                            '6',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__pinball',
                    title: 'Pinball',
                    collapsible: true,
                    blocks: [
                        p('589 streams | 141 streamers | 4.2 stickiness | 29 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__pinball__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '16',
                                            '320',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/backhandpinball', 'BackhandPinball'),
                                            ],
                                            '14',
                                            '223',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/arcadelegacy', 'ArcadeLegacy'),
                                            ],
                                            '14',
                                            '47',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/enteryourinitials', 'EnterYourInitials'),
                                            ],
                                            '14',
                                            '32',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/whatsbrewing', 'whatsbrewing'),
                                            ],
                                            '14',
                                            '15',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__pinball__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/foxcitiespinball', 'Foxcitiespinball'),
                                            ],
                                            '320',
                                            '16',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/backhandpinball', 'BackhandPinball'),
                                            ],
                                            '223',
                                            '14',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/iepinball', 'iepinball')],
                                            '96',
                                            '6',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/electricbatarcade', 'ElectricBatArcade'),
                                            ],
                                            '59',
                                            '5',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/raydaypinball', 'raydaypinball'),
                                            ],
                                            '53',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__fall-guys',
                    title: 'Fall Guys',
                    collapsible: true,
                    blocks: [
                        p('10,872 streams | 4,963 streamers | 2.2 stickiness | 26 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__fall-guys__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/telinha', 'Telinha')],
                                            '203',
                                            '116',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/itstime4war_', 'iTsTiMe4WaR_'),
                                            ],
                                            '46',
                                            '8',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/weedmanyogurt24', 'weedmanyogurt24'),
                                            ],
                                            '38',
                                            '12',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/autoaimttvyt', 'AutoAimTtvYt'),
                                            ],
                                            '38',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/queendarkttvxx', 'queendarkttvxx'),
                                            ],
                                            '37',
                                            '29',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__fall-guys__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                                            ],
                                            '38,816',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/therealmarzaa', 'TheRealMarzaa'),
                                            ],
                                            '5,606',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/erobb221', 'erobb221')],
                                            '3,469',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/yugi', 'Yugi')],
                                            '2,576',
                                            '2',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/bebahan', 'Bebahan')],
                                            '1,108',
                                            '3',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-smash-bros-ultimate',
                    title: 'Super Smash Bros. Ultimate',
                    collapsible: true,
                    blocks: [
                        p('11,898 streams | 3,622 streamers | 3.3 stickiness | 18 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/vgtv_ultimate', 'VGTV_Ultimate'),
                                            ],
                                            '203',
                                            '20',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/metalvayne08', 'metalvayne08'),
                                            ],
                                            '66',
                                            '56',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/kept_varrel', 'kept_VARREL')],
                                            '52',
                                            '154',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/shadowthehedgy', 'shadowthehedgy'),
                                            ],
                                            '52',
                                            '17',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/superdumbnasty', 'SuperDumbNasty'),
                                            ],
                                            '51',
                                            '21',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__super-smash-bros-ultimate__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/etoiles', 'Etoiles')],
                                            '5,606',
                                            '4',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/vgbootcamp', 'VGBootCamp')],
                                            '1,000',
                                            '29',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/reversalgg', 'Reversalgg')],
                                            '773',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/vgbootcamp4', 'vgbootcamp4')],
                                            '731',
                                            '7',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/hungrybox', 'Hungrybox')],
                                            '638',
                                            '9',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__diablo-ii-resurrected',
                    title: 'Diablo II: Resurrected',
                    collapsible: true,
                    blocks: [
                        p('8,656 streams | 1,451 streamers | 6.0 stickiness | 15 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/mrllamavod', 'mrllamavod')],
                                            '202',
                                            '85',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/kahplunk', 'Kahplunk')],
                                            '70',
                                            '12',
                                        ],
                                        [
                                            '3',
                                            [
                                                link('https://twitch.tv/generalbardock', 'GeneralBardock'),
                                            ],
                                            '69',
                                            '17',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/itzshaun420', 'iTzShAuN420')],
                                            '69',
                                            '8',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/devildimlight_ttv', 'DevilDimLight_TTV'),
                                            ],
                                            '66',
                                            '25',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__diablo-ii-resurrected__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [
                                                link('https://twitch.tv/avoidingthepuddle', 'AvoidingThePuddle'),
                                            ],
                                            '1,408',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/mathil1', 'Mathil1')],
                                            '854',
                                            '7',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/mrllamasc', 'MrLlamaSC')],
                                            '715',
                                            '21',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/kano', 'Kano')],
                                            '362',
                                            '33',
                                        ],
                                        [
                                            '5',
                                            [link('https://twitch.tv/aquafps', 'AquaFPS')],
                                            '246',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'game-deep-dives__super-mario-party-jamboree',
                    title: 'Super Mario Party Jamboree',
                    collapsible: true,
                    blocks: [
                        p('1,203 streams | 572 streamers | 2.1 stickiness | 29 avg viewers'),
                    ],
                    sections: [
                        section({
                            id: 'game-deep-dives__super-mario-party-jamboree__streamers-with-most-activity',
                            title: 'Streamers with Most Activity',
                            description: 'Minimum 5 average viewers',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Observations',
                                        'Avg Viewers',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/iamkoreanjj', 'IamKoreanJJ')],
                                            '27',
                                            '7',
                                        ],
                                        [
                                            '2',
                                            [link('https://twitch.tv/wardere', 'WARDERE')],
                                            '19',
                                            '37',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/zackingball', 'zackingball')],
                                            '8',
                                            '75',
                                        ],
                                        [
                                            '4',
                                            [link('https://twitch.tv/bcourt3b', 'BCourt3B')],
                                            '8',
                                            '13',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/wolflinksshadowofficial', 'WolfLinksShadowOfficial'),
                                            ],
                                            '8',
                                            '10',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                        section({
                            id: 'game-deep-dives__super-mario-party-jamboree__streamers-with-biggest-audiences',
                            title: 'Streamers with Biggest Audiences',
                            description: 'Minimum 2 observations',
                            collapsible: true,
                            blocks: [
                                table({
                                    variant: 'report',
                                    columns: [
                                        'Rank',
                                        'Streamer',
                                        'Avg Viewers',
                                        'Observations',
                                    ],
                                    rows: [
                                        [
                                            '1',
                                            [link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY')],
                                            '1,143',
                                            '2',
                                        ],
                                        [
                                            '2',
                                            [
                                                link('https://twitch.tv/larryfishburger', 'LarryFishburger'),
                                            ],
                                            '238',
                                            '2',
                                        ],
                                        [
                                            '3',
                                            [link('https://twitch.tv/nobletofu', 'NobletOfU')],
                                            '192',
                                            '2',
                                        ],
                                        [
                                            '4',
                                            [
                                                link('https://twitch.tv/rickyporterdillon', 'RickyPorterDillon'),
                                            ],
                                            '191',
                                            '3',
                                        ],
                                        [
                                            '5',
                                            [
                                                link('https://twitch.tv/superstarman25', 'SuperStarman25'),
                                            ],
                                            '162',
                                            '2',
                                        ],
                                    ],
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        }),
        section({
            id: 'streaming-patterns',
            title: 'Streaming Patterns',
            collapsible: true,
            sections: [
                section({
                    id: 'streaming-patterns__streaming-patterns-by-day-of-week',
                    title: 'Streaming Patterns by Day of Week',
                    collapsible: true,
                    blocks: [
                        p([
                            text('Saturday had the most streamers broadcasting of any day, but roughly the same total audience as midweek days. Approximately 21,000 viewers tuned in to a party game stream on a given September day, but were split across up to 48% more streams on busy days like Saturdays.'),
                        ]),
                        p([
                            text('A few popular creators raised Monday’s and Tuesday’s averages. '),
                            link('https://twitch.tv/papaplatte', 'Papaplatte'),
                            text(', '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text(', and '),
                            link('https://twitch.tv/batora324', 'バトラ'),
                            text(' drew about 20% of Monday’s audience. On Tuesday, '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text(', '),
                            link('https://twitch.tv/theburntpeanut', 'TheBurntPeanut'),
                            text(', and '),
                            link('https://twitch.tv/fps_shaka', 'fps_shaka'),
                            text(' accounted for a similar share. Without those channels, both days averaged about 24 viewers per stream.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Day', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['Sunday', '984', '24'],
                                ['Monday', '736', '30'],
                                ['Tuesday', '713', '29'],
                                ['Wednesday', '742', '23'],
                                ['Thursday', '713', '26'],
                                ['Friday', '830', '19'],
                                ['Saturday', '1,053', '20'],
                            ],
                        }),
                    ],
                }),
                section({
                    id: 'streaming-patterns__streaming-patterns-by-hour-of-day',
                    title: 'Streaming Patterns by Hour of Day',
                    collapsible: true,
                    blocks: [
                        p([
                            text('English-language streams '),
                            link('/party-central?visualization=heatmap&measure=avgStreamsPerCapture&x=hourUtc&y=dayOfWeekUtc&grain=&from=2026-09-01T00%3A00%3A00.000Z&to=2026-10-01T00%3A00%3A00.000Z&language=en', 'peaked at 01:00 UTC'),
                            text('. In the US, that was 18:00 Pacific and 21:00 Eastern on the previous day. Friday and Saturday evenings had the most English channels online at that hour.'),
                        ]),
                        p([
                            text('The busiest hour overall was 19:00 UTC. German channels peaked then, at 21:00 in Germany. French channels peaked an hour later, at 22:00 in France. Large French audiences helped make 20:00 UTC the hour with the highest average viewers across all languages, at 40 per stream.'),
                        ]),
                        p([
                            text('Japanese channels were busiest at 13:00 UTC, or 22:00 in Japan. Earlier in the evening, at 19:00 local time (10:00 UTC), three large Japanese creators drew nearly half of all viewers across the games we follow.'),
                        ]),
                        table({
                            variant: 'report',
                            columns: ['Hour (UTC)', 'Avg Streams', 'Avg Viewers'],
                            rows: [
                                ['00:00', '1,151', '17'],
                                ['01:00', '1,230', '15'],
                                ['02:00', '1,202', '18'],
                                ['03:00', '1,090', '15'],
                                ['04:00', '910', '14'],
                                ['05:00', '734', '16'],
                                ['06:00', '537', '19'],
                                ['07:00', '458', '19'],
                                ['08:00', '425', '22'],
                                ['09:00', '365', '18'],
                                ['10:00', '349', '33'],
                                ['11:00', '411', '34'],
                                ['12:00', '536', '33'],
                                ['13:00', '621', '23'],
                                ['14:00', '683', '27'],
                                ['15:00', '774', '26'],
                                ['16:00', '759', '29'],
                                ['17:00', '834', '26'],
                                ['18:00', '1,033', '31'],
                                ['19:00', '1,236', '35'],
                                ['20:00', '1,180', '40'],
                                ['21:00', '1,080', '25'],
                                ['22:00', '1,033', '20'],
                                ['23:00', '987', '26'],
                            ],
                        }),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=2026-09-top-party-games-and-streamers.js.map